import { useCallback, useEffect, useMemo, useState } from 'react'
import { SearchInput } from '@/components/ui/SearchInput'
import { Outlet, NavLink, useParams, useSearchParams, useLocation } from 'react-router-dom'
import { useQuery, useQueryClient, useIsFetching } from '@tanstack/react-query'
import { RefreshCw } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Container, ContainerContent } from '@/components/ui/Container'
import { MultiSelectFilter } from '@/components/ui/MultiSelectFilter'
import { getSalesOrderCounts } from '@/services/api'
import { SALES_ORDER_METRIC_COPY } from '@/features/sales-order/salesOrderMetricCopy'
import { salesOrderListQueryKeys } from '@/features/sales-order/salesOrderQueries'

/**
 * The tabs feed their customer filter options up to this shell, the same way the order confirmation
 * overview takes its purchaser options from whichever tab is showing. The option list has to come
 * from the tab because "every customer in this tab" is only known once that tab has been queried.
 */
export type SalesOrderOverviewOutletContext = {
  onCustomerOptions: (opts: string[]) => void
}

function TabBadge({ count }: { count?: number }) {
  if (count === undefined) return null
  return (
    <span className="ml-1.5 rounded-md bg-white/70 px-1.5 py-0.5 text-[10px] font-semibold text-gray-600 group-[.active]:bg-brand-100 group-[.active]:text-brand-800">
      {count}
    </span>
  )
}

export function OverviewPage() {
  const { workflowId } = useParams<{ workflowId: string }>()
  const [searchParams, setSearchParams] = useSearchParams()
  const queryClient = useQueryClient()
  const location = useLocation()
  const base = `/sales-order/${workflowId}/overview`

  const searchQuery = searchParams.get('q') ?? ''
  const selectedCustomers = searchParams.getAll('customer')
  const [searchInput, setSearchInput] = useState(searchQuery)
  const [customerOptions, setCustomerOptions] = useState<string[]>([])

  const { data: counts } = useQuery({
    queryKey: salesOrderListQueryKeys.counts(workflowId ?? ''),
    queryFn: () => getSalesOrderCounts(workflowId!),
    enabled: !!workflowId,
    staleTime: 30_000,
  })

  const activeTab = location.pathname.includes('/done') ? 'done' : 'awaiting-validation'
  const activeQueryKey =
    activeTab === 'done'
      ? salesOrderListQueryKeys.done(workflowId ?? '')
      : salesOrderListQueryKeys.awaitingValidation(workflowId ?? '')

  const isFetchingActive = useIsFetching({ queryKey: activeQueryKey }) > 0

  const commitSearch = useCallback(
    (value: string) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          if (value) next.set('q', value)
          else next.delete('q')
          next.delete('page')
          return next
        },
        { replace: true }
      )
    },
    [setSearchParams]
  )

  useEffect(() => {
    if (searchInput === searchQuery) return
    const timer = setTimeout(() => commitSearch(searchInput), 300)
    return () => clearTimeout(timer)
  }, [searchInput, searchQuery, commitSearch])

  useEffect(() => {
    setSearchInput(searchQuery)
  }, [searchQuery])

  function updateCustomers(values: string[]) {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        next.delete('customer')
        values.forEach((v) => next.append('customer', v))
        next.delete('page')
        return next
      },
      { replace: true }
    )
  }

  function handleRefresh() {
    void queryClient.invalidateQueries({ queryKey: activeQueryKey })
    void queryClient.invalidateQueries({
      queryKey: salesOrderListQueryKeys.counts(workflowId ?? ''),
    })
  }

  function tabHref(tab: string) {
    const params = new URLSearchParams(searchParams)
    params.delete('page')
    const qs = params.toString()
    return `${base}/${tab}${qs ? `?${qs}` : ''}`
  }

  const tabClass = ({ isActive }: { isActive: boolean }) =>
    cn(
      'inline-flex h-8 items-center justify-center whitespace-nowrap rounded-full px-5 text-xs font-semibold transition-all',
      isActive
        ? 'bg-white text-gray-900 shadow-sm ring-1 ring-gray-900/5'
        : 'text-gray-600 hover:bg-white/50 hover:text-gray-900'
    )

  const onCustomerOptions = useCallback((opts: string[]) => {
    setCustomerOptions(opts)
  }, [])

  const outletCtx = useMemo<SalesOrderOverviewOutletContext>(
    () => ({ onCustomerOptions }),
    [onCustomerOptions]
  )

  return (
    <div className="container mx-auto">
      <Container>
        <ContainerContent className="space-y-4">
          <div className="mt-4 flex flex-wrap items-center gap-2">
            {/* Filters — left side */}
            <SearchInput
              className="w-[200px]"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search orders…"
            />
            <MultiSelectFilter
              label="customers"
              options={customerOptions}
              selected={selectedCustomers}
              onChange={updateCustomers}
            />

            {/* Spacer */}
            <div className="flex-1" />

            {/* Refresh button */}
            <button
              onClick={handleRefresh}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700"
              aria-label="Refresh"
            >
              <RefreshCw className={cn('h-4 w-4', isFetchingActive && 'animate-spin')} />
            </button>

            {/* Tabs — right side */}
            <div className="inline-flex h-10 items-center rounded-full border border-input bg-brand-100 p-1 gap-0.5 shrink-0">
              <NavLink to={tabHref('awaiting-validation')} className={tabClass}>
                {SALES_ORDER_METRIC_COPY.awaiting_validation.label}
                <TabBadge count={counts?.awaiting_validation} />
              </NavLink>
              <NavLink to={tabHref('done')} className={tabClass}>
                {SALES_ORDER_METRIC_COPY.done.label}
                <TabBadge count={counts?.done} />
              </NavLink>
            </div>
          </div>

          <Outlet context={outletCtx} />
        </ContainerContent>
      </Container>
    </div>
  )
}
